import React, {FC} from 'react';
import styled from 'styled-components';

import {defineMessage, Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextAreaBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textAreaBox';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {DomainModel} from '../../../models/api/domainModel';
import {ContactDetailsOrContactsManagerPanelId} from '../../../store/panels/accounts/createAccountForm/createAccountFormActions';
import {ContactDetailsPanelCreateAccountSubState} from '../../../store/panels/contacts/contactDetailsPanelSubState';
import {ContactsManagerCreateAccountFormState} from '../../../store/panels/contacts/contactsManager/sidePanel/contactsManagerCreateAccountFormState';
import {Avatar} from '../../common/avatars/avatar';
import {AvatarBackground} from '../../common/avatars/avatarBackground';
import {AvatarInput} from '../../common/createFormInputs/avatarInput';
import {CreateFormInputDisplay} from '../../common/createFormInputs/createFormInputDisplay';
import {EmailDomainsInputContainer} from '../../common/createFormInputs/emailDomainsInputContainer';
import {CreateAccountFormCustomAttributesContainer} from './createAccountFormCustomAttributesContainer';

/*
 * Types.
 */

interface CreateAccountFormProps {
  panelId: ContactDetailsOrContactsManagerPanelId;
  createAccountFormState: ContactsManagerCreateAccountFormState | ContactDetailsPanelCreateAccountSubState;
  updateName: HandlerOf<string>;
  updateDescription: HandlerOf<string>;
  updateAvatarFile: HandlerOf<File>;
  updateDomains: HandlerOf<ReadonlyArray<DomainModel>>;
}

/*
 * Style.
 */

const StyledCreateAccountFormDiv = styled.div`
  padding: 16px;
  overflow: auto;
`;

const StyledNameAndAvatarDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  width: 100%;
`;

const StyledTextBoxContainerDiv = styled.div`
  width: 100%;
`;

const StyledAvatarIconDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/*
 * Component.
 */

export const CreateAccountForm: FC<CreateAccountFormProps> = (props) => {
  const theme = useUserTheme();
  const {panelId, createAccountFormState, updateName, updateDescription, updateAvatarFile, updateDomains} =
    props;
  const localize = useLocalize();

  return (
    <StyledCreateAccountFormDiv>
      <StyledNameAndAvatarDiv>
        <AvatarInput
          avatarFile={createAccountFormState.avatarFile}
          updateAvatarFile={updateAvatarFile}
          placeholder={
            <Avatar size={VisualSizesEnum.EXTRA_LARGE}>
              <AvatarBackground color={theme.greys.shade20} />
              <StyledAvatarIconDiv>
                <Icon name="briefcaseFilled" size={24} color={theme.greys.shade40} />
              </StyledAvatarIconDiv>
            </Avatar>
          }
        />
        <StyledTextBoxContainerDiv>
          <TextBox
            size={VisualSizesEnum.LARGE}
            placeholder={localize(
              defineMessage({
                id: 'create-account-form-add-name',
                message: 'Add name',
              }),
            )}
            value={createAccountFormState.name}
            onChange={(value) => updateName(value)}
            isRequired
            noMargin
            shouldAutoFocus
          />
        </StyledTextBoxContainerDiv>
      </StyledNameAndAvatarDiv>

      <CreateFormInputDisplay
        title={<Trans id="create-account-form-description">Description</Trans>}
        input={
          <TextAreaBox
            placeholder={localize(
              defineMessage({
                id: 'create-account-form-add-description',
                message: 'Add description',
              }),
            )}
            value={createAccountFormState.description}
            onChange={(value) => updateDescription(value)}
            shouldAllowResize
            noMargin
          />
        }
      />

      <EmailDomainsInputContainer domains={createAccountFormState.domains} updateDomains={updateDomains} />

      <CreateAccountFormCustomAttributesContainer
        panelId={panelId}
        customAttributes={createAccountFormState.customAttributes}
      />
    </StyledCreateAccountFormDiv>
  );
};
